import type { TenantPluginUsage } from "../../../../hooks/useAdminStatus";

// Display decisions for the Plugins panel. Kept out of the component so the two
// things a reader gets wrong - what the totals cover, and what "failed" means -
// are unit-testable without rendering.

/**
 * What the "Last pass" cell is entitled to claim.
 *
 * CloudWatch stores aggregated series, so on that path the newest timestamp is
 * the newest non-empty 5-MINUTE BUCKET, and an `error` status means that bucket
 * contained a failure - not that the most recent pass failed. A bucket holding
 * one failure among twenty successes reported "failed", which sent someone
 * chasing an outage that never happened.
 *
 * Only the local path reads the box's own rollup and knows which single pass
 * was last, and it is the presence of `feature` that says so: the AWS path
 * always leaves it null (see AwsTenantPluginUsage). So that field is the honest
 * discriminator for whether we may speak about a pass or only about a bucket.
 */
export type LastPassTone = "never" | "ok" | "failed-pass" | "failed-bucket";

export const lastPassTone = (plugin: TenantPluginUsage): LastPassTone => {
  if (plugin.lastPass === null) return "never";
  if (plugin.lastPass.status !== "error") return "ok";
  return plugin.lastPass.feature !== null ? "failed-pass" : "failed-bucket";
};

export const LAST_PASS_NOTE: Readonly<Record<LastPassTone, string | null>> = {
  never: null,
  ok: null,
  "failed-pass": "failed",
  // Deliberately not "failed": the claim is about the window's newest bucket.
  "failed-bucket": "a pass failed",
};

export const LAST_PASS_TITLE: Readonly<Record<LastPassTone, string | null>> = {
  never: null,
  ok: null,
  "failed-pass": null,
  "failed-bucket":
    "CloudWatch reports usage in 5-minute buckets, so this says the most recent bucket contained a failure - not that the most recent pass failed.",
};

/**
 * The top feature, and how many the row's totals actually span.
 *
 * A bare feature name sitting beside the totals reads as though the totals ARE
 * that feature's - which is exactly how this panel was first misread, and the
 * misreading is easy to land on when only one feature has data in the selected
 * range. Saying "of 2" makes the row's scope legible without a column each.
 */
export type TopFeatureView = { readonly name: string; readonly ofTotal: number | null };

export const topFeature = (plugin: TenantPluginUsage): TopFeatureView | null => {
  if (plugin.busiestFeature === null) return null;
  const total = plugin.features.length;
  return { name: plugin.busiestFeature, ofTotal: total > 1 ? total : null };
};

/**
 * Why a plugin the tenant runs can still show nothing.
 *
 * It deliberately does NOT say which of the two causes applies, because from
 * here that is unknowable. The panel only reaches this when `features` is
 * empty, and on both strategies `features` and `lastPass` are built from the
 * same passes over the same window: AwsTenantPluginUsage only ever records a
 * last-pass timestamp inside the block that has already filled `totals`, and
 * LocalTenantPluginUsage reads both off one rollup file. So an empty `features`
 * implies a null `lastPass`, and an earlier version of this branched on exactly
 * that - one side could never render, and the side that did told a reader whose
 * passes were simply outside the range to sit and wait.
 *
 * Telling the two apart needs a fact from OUTSIDE the selected window, which
 * neither strategy fetches and which would cost a second CloudWatch query per
 * plugin per page load. Naming the window and offering both causes is honest at
 * no cost; asserting the wrong one is what actually misled someone.
 */
export const emptyReason = (windowLabel: string): string =>
  `No passes ${windowLabel}. A newly installed plugin publishes within 5 minutes; otherwise try a wider range.`;
